if (!customElements.get('video-carousel')) {
  customElements.define(
    'video-carousel',
    class VideoCarousel extends HTMLElement {
      constructor() {
        super();

        this.swiper = null;
        this.focusObserver = null;
        this.mobileMediaQuery = window.matchMedia('(max-width: 749px)');
        this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.handleResize = this.handleResize.bind(this);
        this.handleMotionPreference = this.handleMotionPreference.bind(this);
        this.videoListeners = [];
      }

      connectedCallback() {
        this.swiperElement = this.querySelector('.video-carousel__swiper');
        this.scrollbarElement = this.querySelector('.video-carousel__scrollbar');
        this.offsetSource = document.getElementById(this.dataset.offsetSourceId);

        if (!this.swiperElement) return;

        this.slides = Array.from(this.querySelectorAll('.video-carousel__item'));
        this.slideGap = 8;

        this.updateSlideWidths();

        this.initSwiper();
        this.setupVideoInteractions();
        this.setupFocusManagement();
        this.setupViewportObserver();

        window.addEventListener('resize', this.handleResize);
        this.reducedMotionQuery.addEventListener('change', this.handleMotionPreference);
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.handleResize);
        this.reducedMotionQuery.removeEventListener('change', this.handleMotionPreference);

        if (this.focusObserver) {
          this.focusObserver.disconnect();
          this.focusObserver = null;
        }

        if (this.viewportObserver) {
          this.viewportObserver.disconnect();
          this.viewportObserver = null;
        }

        this.videoListeners.forEach(({ element, eventName, listener }) => {
          element.removeEventListener(eventName, listener);
        });
        this.videoListeners = [];

        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }
      }

      initSwiper() {
        if (this.slides.length <= 1 || typeof Swiper === 'undefined') {
          this.classList.add('is-static');
          return;
        }

        this.swiper = new Swiper(this.swiperElement, {
          speed: 500,
          grabCursor: true,
          watchSlidesProgress: true,
          spaceBetween: 8,
          slidesPerView: 'auto',
          slidesOffsetBefore: this.getOffsetLeft(),
          slidesOffsetAfter: this.getOffsetLeft(),
          scrollbar: {
            el: this.scrollbarElement,
            draggable: true,
            dragSize: 'auto',
            hide: false,
            snapOnRelease: false,
          },
          keyboard: {
            enabled: true,
            onlyInViewport: true,
          },
          mousewheel: {
            enabled: true,
            forceToAxis: true,
            releaseOnEdges: true,
            sensitivity: 0.7,
            thresholdDelta: 10,
            thresholdTime: 120,
          },
          breakpoints: {
            750: {
              slidesPerView: 'auto',
              spaceBetween: 8,
            },
          },
          on: {
            resize: () => {
              this.updateOffset();
            },
          },
        });

        // Run once after layout settles to ensure left offset and slide count are
        // correct on first paint — slidesPerViewDynamic() is unreliable before this.
        requestAnimationFrame(() => {
          this.updateOffset();
        });
      }

      setupVideoInteractions() {
        this.slides.forEach((slide) => {
          const media = slide.querySelector('.video-carousel__media');
          const video = slide.querySelector('.video-carousel__video');

          if (!media || !video) return;

          const markVideoReady = () => {
            media.classList.add('is-video-ready');
          };

          const resetVideoReady = () => {
            media.classList.remove('is-video-ready');
          };

          const handleMouseEnter = () => {
            if (this.mobileMediaQuery.matches || this.reducedMotionQuery.matches) return;
            this.playVideo(video, media);
          };

          const handleMouseLeave = () => {
            if (this.mobileMediaQuery.matches) return;
            this.pauseVideo(video, media);
          };

          const handleClick = (event) => {
            if (!this.mobileMediaQuery.matches) return;
            if (event.target.closest('quick-add-card, .quick-add-card')) return;

            if (video.paused) {
              media.dataset.userPaused = 'false';
              this.playVideo(video, media);
            } else {
              media.dataset.userPaused = 'true';
              this.pauseVideo(video, media);
            }
          };

          const handleKeyDown = (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();

            if (video.paused) {
              media.dataset.userPaused = 'false';
              this.playVideo(video, media);
            } else {
              media.dataset.userPaused = 'true';
              this.pauseVideo(video, media);
            }
          };

          const seekToFirstFrame = () => {
            video.currentTime = 0;
          };

          this.addTrackedListener(media, 'mouseenter', handleMouseEnter);
          this.addTrackedListener(media, 'mouseleave', handleMouseLeave);
          this.addTrackedListener(media, 'click', handleClick);
          this.addTrackedListener(media, 'keydown', handleKeyDown);
          this.addTrackedListener(video, 'loadedmetadata', seekToFirstFrame);
          this.addTrackedListener(video, 'playing', markVideoReady);
          this.addTrackedListener(video, 'emptied', resetVideoReady);
        });
      }

      setupViewportObserver() {
        if (!('IntersectionObserver' in window)) return;

        this.viewportObserver = new IntersectionObserver(
          (entries) => {
            if (!this.mobileMediaQuery.matches || this.reducedMotionQuery.matches) return;

            entries.forEach((entry) => {
              const slide = entry.target;
              const media = slide.querySelector('.video-carousel__media');
              const video = slide.querySelector('.video-carousel__video');

              if (!media || !video) return;

              if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
                if (media.dataset.userPaused === 'true') return;
                this.playVideo(video, media);
              } else {
                this.pauseVideo(video, media);
                media.dataset.userPaused = 'false';
              }
            });
          },
          {
            threshold: [0.25, 0.6, 0.8],
          }
        );

        this.slides.forEach((slide) => {
          this.viewportObserver.observe(slide);
        });
      }

      setupFocusManagement() {
        if (!('IntersectionObserver' in window)) return;

        this.slides.forEach((slide) => {
          slide.inert = true;
        });

        this.focusObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              entry.target.inert = !entry.isIntersecting;
            });
          },
          {
            root: this,
            threshold: 0,
          }
        );

        this.slides.forEach((slide) => {
          this.focusObserver.observe(slide);
        });
      }

      playVideo(video, media) {
        video.play().catch(() => {});
        media.setAttribute('aria-pressed', 'true');
      }

      pauseVideo(video, media) {
        video.pause();
        media.setAttribute('aria-pressed', 'false');
      }

      getOffsetLeft() {
        if (!this.offsetSource) return 0;

        const rectLeft = this.offsetSource.getBoundingClientRect().left;
        const sourceStyles = window.getComputedStyle(this.offsetSource);
        const paddingLeft = parseFloat(sourceStyles.paddingLeft) || 0;

        return Math.max(0, Math.round(Math.max(rectLeft, paddingLeft)));
      }

      updateSlideWidths() {
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
        const offsetLeft = this.getOffsetLeft();
        const availableWidth = Math.max(0, viewportWidth - offsetLeft);

        const desktopWidth = Math.max(120, (availableWidth - this.slideGap * 3) / 3.25);
        const mobileWidth = Math.max(120, (availableWidth - this.slideGap) / 1.15);

        this.style.setProperty('--video-carousel-slide-width-desktop', `${desktopWidth}px`);
        this.style.setProperty('--video-carousel-slide-width-mobile', `${mobileWidth}px`);
      }

      updateOffset() {
        if (!this.swiper) return;

        this.updateSlideWidths();
        const offset = this.getOffsetLeft();
        this.swiper.params.slidesOffsetBefore = offset;
        this.swiper.params.slidesOffsetAfter = offset;
        this.swiper.update();
      }

      handleResize() {
        this.updateOffset();
      }

      handleMotionPreference() {
        if (!this.reducedMotionQuery.matches) return;

        this.slides.forEach((slide) => {
          const media = slide.querySelector('.video-carousel__media');
          const video = slide.querySelector('.video-carousel__video');
          if (!media || !video) return;
          this.pauseVideo(video, media);
        });
      }

      addTrackedListener(element, eventName, listener) {
        element.addEventListener(eventName, listener);
        this.videoListeners.push({ element, eventName, listener });
      }
    }
  );
}
